import { keepPreviousData, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { API_TAGS, queryClient } from '@/shared/api';
import { createStopWord, deleteStopWord, fetchStopWords, updateStopWord } from './api';
import type { StopWordCreate, StopWordListParams, StopWordUpdate } from './types';

/**
 * Query keys for stop words
 */
export const stopWordKeys = {
  all: [API_TAGS.STOP_WORDS] as const,
  lists: () => [...stopWordKeys.all, 'list'] as const,
  list: (params?: StopWordListParams) => [...stopWordKeys.lists(), { ...params }] as const,
  details: () => [...stopWordKeys.all, 'detail'] as const,
  detail: (id: number) => [...stopWordKeys.details(), id] as const,
};

/**
 * Hook to fetch stop words list
 */
export const useStopWords = (params?: StopWordListParams) => {
  return useQuery({
    queryKey: stopWordKeys.list(params),
    queryFn: () => fetchStopWords(params),
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook to fetch stop words with infinite scroll (reverse scroll - loads previous pages when scrolling up)
 */
export const useInfiniteStopWords = (params?: StopWordListParams & { limit?: number; offset?: number }) => {
  const limit = params?.limit || 50;
  const initialPageParam = Math.floor((params?.offset || 0) / limit);

  const query = useInfiniteQuery({
    queryKey: stopWordKeys.list(params),
    queryFn: ({ pageParam }) => fetchStopWords({ ...params, limit, offset: pageParam * limit }),
    initialPageParam,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      return lastPage.total <= limit * (lastPageParam + 1) ? undefined : lastPageParam + 1;
    },
  });

  const data = useMemo(
    () => ({ total: query.data?.pages[0]?.total, stopWords: query.data?.pages.flatMap((p) => p.stopWords) }),
    [query.data],
  );

  return {
    ...query,
    data,
  };
};

/**
 * Hook to create a stop word
 */
export const useCreateStopWord = () => {
  return useMutation({
    mutationFn: (payload: StopWordCreate) => createStopWord(payload),
  });
};

/**
 * Hook to update a stop word
 */
export const useUpdateStopWord = () => {
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: StopWordUpdate }) => updateStopWord({ id, payload }),
  });
};

/**
 * Hook to delete a stop word
 */
export const useDeleteStopWord = () => {
  return useMutation({
    mutationFn: (id: number) => deleteStopWord(id),
    onSuccess: (_, deletedId) => {
      // Remove from detail cache
      queryClient.removeQueries({ queryKey: stopWordKeys.detail(deletedId) });
    },
  });
};
